import { layers } from "@/configs/map";
import type { StaticLayerKey } from "@/configs/map/layers";
import { useMapStore } from "@/stores/map";
import type { TranslationKeys } from "@/types/services/locale";
import { TREE_NODE_IDS } from "@/utils/constants/map";
import { naturalCompare } from "@/utils/sort";
import type { ProcessedGroup } from "../hooks/useProcessedGroups";
import type { LayerTreeNodeData } from "../types";

/**
 * Build static layers tree node
 * Simple folder structure with static layer children
 */
export function buildStaticLayersNode(
  t: <K extends keyof TranslationKeys>(key: K) => TranslationKeys[K],
  toggleStaticLayer: (value: string) => void
): LayerTreeNodeData {
  const workspaceTranslations = t("workspace") as TranslationKeys["workspace"];
  return {
    value: TREE_NODE_IDS.STATIC_LAYERS,
    label: "Static Layers",
    type: "folder",
    children: Object.keys(layers)
      .map((layer) => {
        const layerKey = layer as StaticLayerKey;
        const translationKey = layerKey as keyof TranslationKeys["workspace"];
        return {
          value: layerKey,
          label: (workspaceTranslations[translationKey] as string) || layer,
          type: "layer" as const,
          onClick: toggleStaticLayer,
        };
      })
      .sort((a, b) => naturalCompare(a.label, b.label)),
  };
}

/**
 * Build my data tree node with groups/chunks
 * Simplified structure: My Data folder -> Data layer -> Column selector -> Groups/Chunks
 * Groups and chunks are treated identically in the tree structure
 * For brick layers, count-based groups are shown directly under the data layer
 */
export function buildMyDataNode(
  inputDataName: string | null | undefined,
  groupColumnName: string | null,
  processedGroups: ProcessedGroup[],
  toggleDataLayer: (value: string) => void,
  updateLayerVisibility: (layerId: string, visible: boolean) => void,
  activeDataflowId: string | null | undefined,
  visualizationMode?: "points" | "layers",
  visualizationLayerType?: "municipalities" | "countries" | "timezones" | null
): LayerTreeNodeData {
  // Build group/chunk children - treat them identically
  const groupChildren: LayerTreeNodeData[] = processedGroups.map((group) => {
    const layerId =
      group.type === "chunk"
        ? `chunk:${activeDataflowId}:${group.id}`
        : `group:${activeDataflowId}:${group.id}`;

    return {
      value: group.id,
      label: group.displayName || group.label,
      type: group.type === "chunk" ? "chunk" : "layer",
      color: group.color,
      shape: group.shape,
      onClick: () => {
        // Toggle visibility using unified layer visibility
        // Get current visibility state and toggle it
        const currentState = useMapStore.getState();
        const isVisible = currentState.getLayerVisibilityById(layerId);
        updateLayerVisibility(layerId, !isVisible);
      },
    };
  });

  // Sort groups/chunks by label
  // For brick layer regions, extract point count from label (format: "Name (count)") and sort by count descending only
  // For other groups, sort naturally
  groupChildren.sort((a, b) => {
    const labelA = String(a.label || "");
    const labelB = String(b.label || "");
    
    // Check if labels match brick layer region format: "Name (count)" or "regionId (count)"
    const brickLayerPattern = /^(.+)\s+\((\d+)\)$/;
    const matchA = labelA.match(brickLayerPattern);
    const matchB = labelB.match(brickLayerPattern);
    
    if (matchA && matchB) {
      // Both are brick layer regions - sort by count (descending) only
      const countA = parseInt(matchA[2], 10);
      const countB = parseInt(matchB[2], 10);
      
      return countB - countA; // Descending order (highest count first)
    }
    
    // Fallback to natural sort for non-brick-layer groups
    return naturalCompare(labelA, labelB);
  });

  // Build visualization mode selector node
  const visualizationModeNode: LayerTreeNodeData = {
    value: TREE_NODE_IDS.VISUALIZATION_MODE,
    label: "Visualization Mode",
    type: "visualization",
  };

  // For brick layers, show count groups directly under data layer (no column selector)
  const isBrickLayerMode = visualizationMode === "layers" && visualizationLayerType;
  
  if (isBrickLayerMode) {
    // Brick layer mode: show visualization mode and count groups directly under data layer
    const dataLayerChildren: LayerTreeNodeData[] = [visualizationModeNode];
    if (groupChildren.length > 0) {
      dataLayerChildren.push(...groupChildren);
    }
    
    return {
      value: TREE_NODE_IDS.MY_DATA,
      label: "My data",
      type: "folder",
      children: [
        {
          value: TREE_NODE_IDS.MY_DATA_INPUT,
          label: inputDataName || "My Data",
          type: "layer",
          onClick: toggleDataLayer,
          children: dataLayerChildren,
        },
      ],
    };
  }

  // Column-based mode: show column selector with visualization mode and groups
  const columnSelectorChildren: LayerTreeNodeData[] = [visualizationModeNode];
  if (groupChildren.length > 0) {
    columnSelectorChildren.push(...groupChildren);
  }

  const columnSelectorNode: LayerTreeNodeData = {
    value: TREE_NODE_IDS.MY_DATA_INPUT_GROUP,
    label: groupColumnName ? `Group by: ${groupColumnName}` : "Group by column",
    type: "group",
    children: columnSelectorChildren,
  };

  return {
    value: TREE_NODE_IDS.MY_DATA,
    label: "My data",
    type: "folder",
    children: [
      {
        value: TREE_NODE_IDS.MY_DATA_INPUT,
        label: inputDataName || "My Data",
        type: "layer",
        onClick: toggleDataLayer,
        children: [columnSelectorNode],
      },
    ],
  };
}
